import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { themeClass } from "../components/ThemeChip";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Loader2, BookOpen, MessagesSquare, Sparkles, Clock, NotebookPen } from "lucide-react";

const STEPS = [
  { key: "story", label: "Story", icon: BookOpen },
  { key: "conversation", label: "Conversation", icon: MessagesSquare },
  { key: "activity", label: "Activity", icon: Sparkles },
  { key: "reflection", label: "Reflection", icon: NotebookPen },
];

function StepHeader({ active, theme }) {
  return (
    <div className={`flex items-center gap-2 ${themeClass(theme)}`}>
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex-1 flex items-center gap-2">
          <div className={`h-1 flex-1 rounded-full transition-colors ${i <= active ? "theme-accent-bg" : "bg-muted"}`}/>
        </div>
      ))}
    </div>
  );
}

function StepShell({ active, theme, onExit, onBack, children }) {
  return (
    <div className={`min-h-screen ${themeClass(theme)} relative`}>
      <div className="absolute inset-0 theme-surface opacity-30"/>
      <div className="relative max-w-2xl mx-auto px-6 pt-6 pb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1">
            <button onClick={onExit} className="p-2 rounded-full hover:bg-white/70" data-testid="session-exit-btn" aria-label="Pause">
              <X className="w-5 h-5"/>
            </button>
            {active > 0 && (
              <button onClick={onBack} className="p-2 rounded-full hover:bg-white/70" data-testid="session-back-btn" aria-label="Back">
                <ArrowLeft className="w-5 h-5"/>
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
            <Clock className="w-3 h-3"/> 30 minutes · Step {active + 1} of 4
          </div>
        </div>
        <StepHeader active={active} theme={theme}/>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

function InlineCTA({ children, onClick, testId, disabled, variant = "primary" }) {
  return (
    <div className="mt-8">
      <button onClick={onClick} disabled={disabled}
              className={`${variant === "primary" ? "btn-primary" : "btn-ghost"} w-full text-lg disabled:opacity-60`}
              data-testid={testId}>{children}</button>
    </div>
  );
}

function StoryStep({ data, onNext }) {
  const story = data.session.personalized?.story || {};
  const imgSrc = story.image_url
    ? (story.image_url.startsWith("http")
        ? story.image_url
        : `${process.env.REACT_APP_BACKEND_URL}${story.image_url}`)
    : null;
  return (
    <div className="rise-in" data-testid="session-story-step">
      <div className="text-xs uppercase tracking-widest theme-text mb-3">Step 1 · Story · 5–7 min</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-7 leading-tight">{story.title}</h1>
      {imgSrc && (
        <div
          className="w-full mb-6 rounded-2xl overflow-hidden shadow-sm relative"
          style={{
            aspectRatio: "16 / 9",
            backgroundColor: "#FFFBF5",
          }}
          data-testid="session-story-image-frame"
        >
          <img
            src={imgSrc}
            alt={story.title || "Story illustration"}
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "contain", objectPosition: "center" }}
            data-testid="session-story-image"
            onError={(e) => {
              // Broken URL. hide the whole frame gracefully.
              e.currentTarget.parentElement.style.display = "none";
            }}
            loading="eager"
          />
        </div>
      )}
      <article className="reader whitespace-pre-wrap">{story.content_md}</article>
      <InlineCTA onClick={onNext} testId="session-story-done">
        Done reading <ArrowRight className="w-4 h-4"/>
      </InlineCTA>
    </div>
  );
}

function ConversationStep({ data, onNext }) {
  const qs = data.session.personalized?.questions || [];
  const [idx, setIdx] = useState(0);
  const q = qs[idx];
  if (!q) {
    return (
      <div>
        <p className="text-muted-foreground mb-6">No questions for this week.</p>
        <InlineCTA onClick={onNext} testId="session-conv-skip">Continue</InlineCTA>
      </div>
    );
  }
  return (
    <div className="rise-in" data-testid="session-conversation-step">
      <div className="text-xs uppercase tracking-widest theme-text mb-3">Step 2 · Conversation · 10–12 min</div>
      <h2 className="font-display text-3xl mb-2 leading-tight">A question to share</h2>
      <p className="text-sm text-muted-foreground mb-7">Read it aloud. Pause. Listen.</p>

      <div className="card-soft p-8 sm:p-10">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          Question {idx + 1} of {qs.length}
        </div>
        <p className="font-display text-3xl leading-snug">{q.text}</p>
        {q.hint && <p className="mt-6 text-sm text-muted-foreground italic">Hint for parent: {q.hint}</p>}
      </div>

      <div className="flex gap-3 mt-6">
        <button disabled={idx === 0} onClick={() => setIdx(idx - 1)}
                className="btn-ghost disabled:opacity-50" data-testid="session-conv-prev">Previous</button>
        {idx < qs.length - 1 ? (
          <button onClick={() => setIdx(idx + 1)} className="btn-primary flex-1"
                  data-testid="session-conv-next">Next question <ArrowRight className="w-4 h-4"/></button>
        ) : (
          <button onClick={onNext} className="btn-primary flex-1" data-testid="session-conv-done">
            We talked it out <ArrowRight className="w-4 h-4"/>
          </button>
        )}
      </div>
    </div>
  );
}

function ActivityStep({ data, onNext }) {
  const a = data.session.personalized?.activity || {};
  // Parse things_needed into bullet lines (supports newline OR comma separated)
  const materials = (a.things_needed || "")
    .split(/\r?\n|·|•/)
    .flatMap((s) => s.split(/,(?=\s)/))
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="rise-in" data-testid="session-activity-step">
      <div className="text-xs uppercase tracking-widest theme-text mb-3">Step 3 · Activity · {a.estimated_minutes || 10} min</div>
      <h1 className="font-display text-4xl mb-5 leading-tight">{a.name}</h1>

      {a.objective && (
        <div className="card-soft p-5 mb-4" data-testid="session-activity-objective">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Why we're doing this
          </div>
          <p className="text-base leading-relaxed">{a.objective}</p>
        </div>
      )}

      <div className="card-soft p-5 mb-4" data-testid="session-activity-things-needed">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Things you'll need
        </div>
        {materials.length > 0 ? (
          <ul className="space-y-1.5 text-base">
            {materials.map((m, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[hsl(var(--neu-orange))] shrink-0"/>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-base italic text-muted-foreground">No special materials required.</p>
        )}
      </div>

      <div className="card-soft p-7">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
          How to do it
        </div>
        <article className="reader text-[1.2rem] sm:text-[1.3rem] whitespace-pre-wrap">{a.instructions_md}</article>
      </div>
      <InlineCTA onClick={onNext} testId="session-activity-done">
        Activity done <ArrowRight className="w-4 h-4"/>
      </InlineCTA>
    </div>
  );
}

function ReflectionStep({ data, onSubmit, busy }) {
  const qs = data.reflection_questions || [];
  const [answers, setAnswers] = useState(() => qs.reduce((a, q) => ({ ...a, [q.id]: "" }), {}));
  useEffect(() => {
    setAnswers((prev) => qs.reduce((a, q) => ({ ...a, [q.id]: prev[q.id] || "" }), {}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.length]);

  const canSubmit = useMemo(() => Object.values(answers).some((v) => v.trim().length >= 1), [answers]);

  return (
    <div className="rise-in" data-testid="session-reflection-step">
      <div className="text-xs uppercase tracking-widest theme-text mb-3">Step 4 · Reflection · 3–5 min</div>
      <h1 className="font-display text-4xl mb-2 leading-tight">A word from you</h1>
      <p className="text-muted-foreground mb-7">Three short reflections. Just for you, just for now.</p>

      <div className="space-y-5">
        {qs.map((q, i) => (
          <div key={q.id} className="card-soft p-5">
            <label className="font-display text-xl block mb-2">{q.question_text}</label>
            <textarea rows={3} value={answers[q.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      className="field" placeholder="A few words…"
                      data-testid={`reflection-input-${q.order || i + 1}`}/>
          </div>
        ))}
      </div>

      <InlineCTA onClick={() => onSubmit(answers)} disabled={!canSubmit || busy}
                  testId="session-reflection-submit">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving…</> : "Save and celebrate"}
      </InlineCTA>
      <p className="text-xs text-muted-foreground text-center mt-3">
        Type at least one reflection. even a few words count.
      </p>
    </div>
  );
}

export default function SessionPlayer() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/session/current");
        setData(r.data);
        const cur = r.data.session.current_step;
        const idx = STEPS.findIndex((s) => s.key === cur);
        if (idx >= 0 && !r.data.session.completed_at) setStep(Math.max(idx, 0));
      } catch (err) {
        // 402 = trial complete / subscription required → redirect to paywall.
        if (err?.response?.status === 402) {
          nav("/paywall", {
            replace: true,
            state: { reason: "trial_complete", access: err.response?.data?.detail?.access },
          });
          return;
        }
        setLoadFailed(true);
        toast.error(err?.response?.data?.detail?.message
          || err?.response?.data?.detail
          || "Could not load session");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadFailed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-muted-foreground mb-4">We couldn't load your session.</p>
        <button onClick={() => nav("/home")} className="btn-primary" data-testid="session-back-home">Back to home</button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;

  const theme = data.theme?.color_token || "default";
  const sess = data.session;

  const completeStep = async (key) => {
    try { await api.post(`/session/${sess.id}/step/${key}`); } catch {}
  };

  const onNext = async (currentKey) => {
    await completeStep(currentKey);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const onBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitReflection = async (answers) => {
    setBusy(true);
    try {
      const payload = { answers: Object.entries(answers)
        .filter(([, t]) => t && t.trim())
        .map(([qid, txt]) => ({ question_id: qid, answer_text: txt })) };
      const r = await api.post(`/session/${sess.id}/reflection`, payload, { timeout: 45000 });
      nav("/session/complete", { state: { result: r.data, theme: data.theme, week: data.week } });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || err?.message || "Could not save. Please try again.");
    } finally { setBusy(false); }
  };

  const onExit = () => {
    if (window.confirm("Pause this session? You can resume later from where you left off.")) nav("/home");
  };

  return (
    <StepShell active={step} theme={theme} onExit={onExit} onBack={onBack}>
      {step === 0 && <StoryStep data={data} onNext={() => onNext("story")} />}
      {step === 1 && <ConversationStep data={data} onNext={() => onNext("conversation")} />}
      {step === 2 && <ActivityStep data={data} onNext={() => onNext("activity")} />}
      {step === 3 && <ReflectionStep data={data} onSubmit={submitReflection} busy={busy} />}
    </StepShell>
  );
}
